import { makeAutoObservable, runInAction } from 'mobx';
import { io } from 'socket.io-client';
import jwtDecode from 'jwt-decode';
import { GetMyChats, CreateChat, GetChatMessages, EditMessage, DeleteMessage, ToggleReaction, ForwardMessage } from '../http/ChatAPI';
import { 
  playSound, 
  showNotification, 
  updateDocumentTitle,
  requestNotificationPermission 
} from '../utils/notifications';

export default class ChatStore {
  socket = null;
  chats = [];
  activeChat = null;
  messages = [];
  typingUsers = new Map();
  isWidgetOpen = false;
  isLoading = false;
  currentUserId = null;
  editingMessage = null;
  messageStatuses = new Map(); 
  replyingTo = null;
  contextMenu = null;
  showForwardModal = false;
  forwardMessageId = null;

  mobileTab = 'chats';
  chatGroupFilter = 'all';

  view = 'chats';
  isMobile = false;
  showLeftSidebar = true;
  showRightSidebar = false;

  constructor() {
    makeAutoObservable(this);
    
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    this.isMobile = mediaQuery.matches;
    
    this._mediaQueryHandler = (e) => {
      runInAction(() => {
        this.isMobile = e.matches;
        console.log('📱 Mobile mode:', e.matches);
      });
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', this._mediaQueryHandler);
    } else {
      mediaQuery.addListener(this._mediaQueryHandler);
    }
    
    // === БЕЗОПАСНОЕ ДЕКОДИРОВАНИЕ ТОКЕНА ===
    try {
      const token = localStorage.getItem('token');
      if (token && token !== 'undefined' && token !== 'null' && token.split('.').length === 3) {
        const decoded = jwtDecode(token);
        this.currentUserId = decoded.id;
        console.log('👤 Current user ID from token:', this.currentUserId);
      } else {
        console.log('⚠️ No valid token found');
        this.currentUserId = null;
      }
    } catch (e) {
      console.error('❌ Error decoding token:', e.message);
      this.currentUserId = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }

  destroy() {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener('change', this._mediaQueryHandler);
    } else {
      mediaQuery.removeListener(this._mediaQueryHandler);
    }
  }

  checkMobile() {
    runInAction(() => {
      this.isMobile = window.innerWidth < 768;
    });
  }

  setView(view) {
    runInAction(() => {
      this.view = view;
    });
  }

  toggleLeftSidebar() {
    runInAction(() => {
      this.showLeftSidebar = !this.showLeftSidebar;
    });
  }

  toggleRightSidebar() {
    runInAction(() => {
      this.showRightSidebar = !this.showRightSidebar;
    });
  }

  setMobileTab(tab) {
    runInAction(() => {
      this.mobileTab = tab;
      if (tab !== 'chats') {
        this.activeChat = null;
        this.messages = [];
      }
    });
  }

  setChatGroupFilter(filter) {
    runInAction(() => {
      this.chatGroupFilter = filter;
    });
  }

  connect() {
    if (this.socket && this.socket.connected) {
      console.log('⚠️ Socket already connected');
      return;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    const token = localStorage.getItem('token');
    
    // === ЗАЩИТА ОТ НЕВАЛИДНОГО ТОКЕНА ===
    if (!token || token === 'undefined' || token === 'null' || token.split('.').length !== 3) {
      console.warn('⚠️ No valid token found for socket connection');
      return;
    }

    try {
      const decoded = jwtDecode(token);
      this.currentUserId = decoded.id;
      console.log('👤 Current user ID from token:', this.currentUserId);
    } catch (e) {
      console.error('❌ Error decoding token:', e.message);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return;
    }

    const socketUrl = process.env.REACT_APP_API_URL || 'https://olap.ak-i.ru';
    console.log('🔌 Connecting to socket:', socketUrl);

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
      upgrade: false,
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket.id, 'transport:', this.socket.io.engine.transport.name);
    });

    this.socket.on('connect_error', (err) => {
      console.error('❌ Socket connection error:', err.message);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
    });

    this.socket.on('reaction_update', (data) => {
      console.log('😀 Reaction update from socket:', data);
      
      if (data.userId === this.currentUserId) {
        console.log('⏭️ Skipping own reaction update');
        return;
      }
      
      runInAction(() => {
        const msgIndex = this.messages.findIndex(m => m.id === data.messageId);
        if (msgIndex !== -1) {
          const oldMsg = this.messages[msgIndex];
          const oldReactions = oldMsg.reactions || [];
          
          let newReactions;
          
          if (data.action === 'removed') {
            newReactions = oldReactions.filter(r => 
              !(r.userId === data.userId && r.emoji === data.emoji)
            );
          } else if (data.action === 'replaced') {
            newReactions = oldReactions.filter(r => r.userId !== data.userId);
            
            const newReaction = {
              id: Date.now().toString(),
              messageId: data.messageId,
              userId: data.userId,
              emoji: data.emoji,
              user: data.user ? {
                id: data.user.id,
                email: data.user.email || '',
                persons_info: {
                  name: data.user.persons_info?.name || data.user.name || '',
                  surname: data.user.persons_info?.surname || data.user.surname || ''
                }
              } : null
            };
            
            newReactions = [...newReactions, newReaction];
          } else if (data.action === 'added') {
            newReactions = oldReactions.filter(r => r.userId !== data.userId);
            
            const newReaction = {
              id: Date.now().toString(),
              messageId: data.messageId,
              userId: data.userId,
              emoji: data.emoji,
              user: data.user ? {
                id: data.user.id,
                email: data.user.email || '',
                persons_info: {
                  name: data.user.persons_info?.name || data.user.name || '',
                  surname: data.user.persons_info?.surname || data.user.surname || ''
                }
              } : null
            };
            
            newReactions = [...newReactions, newReaction];
          }
          
          const newMsg = {
            id: oldMsg.id,
            chatId: oldMsg.chatId,
            senderId: oldMsg.senderId,
            text: oldMsg.text,
            type: oldMsg.type,
            replyTo: oldMsg.replyTo,
            forwardedFrom: oldMsg.forwardedFrom,
            isEdited: oldMsg.isEdited,
            isDeleted: oldMsg.isDeleted,
            status: oldMsg.status,
            createdAt: oldMsg.createdAt,
            updatedAt: oldMsg.updatedAt,
            sender: oldMsg.sender ? { ...oldMsg.sender } : null,
            parentMessage: oldMsg.parentMessage ? { ...oldMsg.parentMessage } : null,
            reactions: newReactions
          };
          
          this.messages[msgIndex] = newMsg;
          
          console.log('✅ Message updated from socket, reactions:', newReactions.length);
        }
      });
    });

    this.socket.on('message_status', ({ messageId, messageIds, status }) => {
      console.log('📊 Message status:', status, messageId || messageIds);
      
      runInAction(() => {
        if (messageId) {
          this.messageStatuses.set(messageId, status);
        }
        if (messageIds && Array.isArray(messageIds)) {
          messageIds.forEach(id => {
            this.messageStatuses.set(id, status);
          });
        }
      });
    });

    this.socket.on('new_message', (msg) => {
      console.log('📩 Received new_message:', msg);
      
      const isOwnMessage = String(msg.senderId) === String(this.currentUserId);
      const isTabActive = document.visibilityState === 'visible';
      const isChatOpen = this.activeChat?.id === msg.chatId;
      
      runInAction(() => {
        const chatIndex = this.chats.findIndex(c => c.id === msg.chatId);
        if (chatIndex !== -1) {
          this.chats[chatIndex].lastMessage = msg;
          this.chats[chatIndex].lastMessageAt = msg.createdAt;
          
          if (!isChatOpen && !isOwnMessage) {
            this.chats[chatIndex].unreadCount = (this.chats[chatIndex].unreadCount || 0) + 1;
          }
          
          this.chats.sort((a, b) => {
            const dateA = new Date(a.lastMessageAt || a.createdAt);
            const dateB = new Date(b.lastMessageAt || b.createdAt);
            return dateB - dateA;
          });
        }
        
        updateDocumentTitle(this.totalUnread);
        
        if (!isOwnMessage) {
          if (!isTabActive) {
            playSound();
          }
          
          const senderName = msg.sender?.persons_info?.name 
            ? `${msg.sender.persons_info.name} ${msg.sender.persons_info.surname || ''}`.trim()
            : msg.sender?.email || 'Собеседник';
          
          const chatName = this.chats[chatIndex]?.type === 'private'
            ? senderName
            : this.chats[chatIndex]?.name || 'Чат';
          
          showNotification(
            `💬 ${chatName}`,
            `${senderName}: ${msg.text}`
          );
        }
        
        if (isOwnMessage) {
          if (this.activeChat?.id === msg.chatId) {
            const tempIndex = this.messages.findIndex(m => 
              m.id.startsWith('temp_') && m.text === msg.text
            );
            
            if (tempIndex !== -1) {
              this.messages[tempIndex] = msg;
              console.log('✅ Replaced temp message with real one');
            } else {
              if (!this.messages.find(m => m.id === msg.id)) {
                this.messages.push(msg);
              }
            }
          }
          console.log('⏭️ Skipping own message in notifications');
          return;
        }
        
        if (isChatOpen) {
          if (!this.messages.find(m => m.id === msg.id)) {
            this.messages.push(msg);
            console.log('✅ Added message from other user');
          }
          this.socket.emit('mark_read', { chatId: msg.chatId });
          this.socket.emit('message_delivered', {
            messageId: msg.id,
            senderId: msg.senderId,
            chatId: msg.chatId
          });
        } else {
          this.socket.emit('message_delivered', {
            messageId: msg.id,
            senderId: msg.senderId,
            chatId: msg.chatId
          });
        }
      });
    });

    this.socket.on('user_typing', ({ chatId, userId, userName, userSurname }) => {
      runInAction(() => {
        if (this.activeChat?.id === chatId) {
          this.typingUsers.set(userId, {
            name: userName || '',
            surname: userSurname || ''
          });
          
          setTimeout(() => {
            runInAction(() => this.typingUsers.delete(userId));
          }, 3000);
        }
      });
    });

    this.socket.on('user_stop_typing', ({ chatId, userId }) => {
      runInAction(() => {
        if (this.activeChat?.id === chatId) {
          this.typingUsers.delete(userId);
        }
      });
    });

    this.socket.on('message_edited', (msg) => {
      console.log('✏️ Received message_edited:', msg);
      if (!msg || !msg.id) return;
      
      runInAction(() => {
        const msgIndex = this.messages.findIndex(m => m && m.id === msg.id);
        if (msgIndex !== -1) {
          this.messages[msgIndex] = msg;
        }
      });
    });

    this.socket.on('message_deleted', (msg) => {
      console.log('🗑️ Received message_deleted:', msg);
      if (!msg || !msg.id) return;
      
      runInAction(() => {
        const msgIndex = this.messages.findIndex(m => m && m.id === msg.id);
        if (msgIndex !== -1) {
          this.messages[msgIndex] = msg;
        }
      });
    });

    this.socket.on('messages_read', ({ chatId, userId }) => {
      runInAction(() => {
        if (this.activeChat?.id === chatId) {
          this.messages.forEach(m => {
            if (m.senderId === userId) m.isRead = true;
          });
        }
      });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  async loadChats() {
    try {
      runInAction(() => { this.isLoading = true; });
      const data = await GetMyChats();
      runInAction(() => { 
        this.chats = data.chats || [];
        this.isLoading = false;
      });
    } catch (e) {
      console.error('Error loading chats:', e);
      runInAction(() => { this.isLoading = false; });
    }
  }

  async createChat(name, type, memberIds) {
    try {
      console.log('📝 Creating chat:', { name, type, memberIds });
      const data = await CreateChat(name, type, memberIds);
      console.log('✅ Chat created:', data);
      
      const newChat = data.chat;
      const isExisting = data.existing || false;
      
      if (this.socket && newChat) {
        this.socket.emit('join_chat', newChat.id);
      }
      
      await this.loadChats();
      
      if (newChat) {
        await this.openChat(newChat);
      }
      
      return { ...newChat, existing: isExisting };
    } catch (e) {
      console.error('❌ Error creating chat:', e);
      console.error('❌ Error details:', e.response?.data || e.message);
      throw e;
    }
  }

  async editMessage(messageId, newText) {
    if (!newText.trim()) return;
    
    runInAction(() => {
      const msgIndex = this.messages.findIndex(m => m && m.id === messageId);
      if (msgIndex !== -1) {
        this.messages[msgIndex] = {
          ...this.messages[msgIndex],
          text: newText.trim(),
          isEdited: true
        };
      }
      this.editingMessage = null;
    });

    try {
      const data = await EditMessage(messageId, newText);
      runInAction(() => {
        if (data && data.message) {
          const msgIndex = this.messages.findIndex(m => m && m.id === data.message.id);
          if (msgIndex !== -1) {
            this.messages[msgIndex] = data.message;
          }
        }
      });
      
      if (this.socket) {
        this.socket.emit('edit_message', { 
          messageId, 
          text: newText.trim() 
        });
      }
    } catch (e) {
      console.error('Error editing message:', e);
      await this.openChat(this.activeChat);
    }
  }

  async deleteMessage(messageId) {
    runInAction(() => {
      const msgIndex = this.messages.findIndex(m => m && m.id === messageId);
      if (msgIndex !== -1) {
        this.messages[msgIndex] = {
          ...this.messages[msgIndex],
          text: 'Сообщение удалено',
          isDeleted: true,
          type: 'system'
        };
      }
    });

    try {
      const data = await DeleteMessage(messageId);
      runInAction(() => {
        if (data && data.message) {
          const msgIndex = this.messages.findIndex(m => m && m.id === data.message.id);
          if (msgIndex !== -1) {
            this.messages[msgIndex] = data.message;
          }
        }
      });
      
      if (this.socket) {
        this.socket.emit('delete_message', { messageId });
      }
    } catch (e) {
      console.error('Error deleting message:', e);
      await this.openChat(this.activeChat);
    }
  }

  startEditing(message) {
    runInAction(() => {
      this.editingMessage = message;
    });
  }

  cancelEditing() {
    runInAction(() => {
      this.editingMessage = null;
    });
  }

  setReplyingTo(message) {
    runInAction(() => {
      this.replyingTo = message;
    });
  }

  cancelReply() {
    runInAction(() => {
      this.replyingTo = null;
    });
  }

  showContextMenu(x, y, message) {
    runInAction(() => {
      this.contextMenu = { x, y, message };
    });
  }

  hideContextMenu() {
    runInAction(() => {
      this.contextMenu = null;
    });
  }

  async toggleReaction(messageId, emoji) {
    try {
      console.log('😀 Toggling reaction:', messageId, emoji);
      
      const response = await ToggleReaction(messageId, emoji);
      const data = response.data;
      
      console.log('✅ Reaction API data:', data);
      
      runInAction(() => {
        const msgIndex = this.messages.findIndex(m => m.id === messageId);
        if (msgIndex !== -1) {
          const oldMsg = this.messages[msgIndex];
          const oldReactions = oldMsg.reactions || [];
          
          let newReactions;
          
          if (data.action === 'removed') {
            newReactions = oldReactions.filter(r => 
              !(r.userId === this.currentUserId && r.emoji === data.emoji)
            );
            console.log('🗑️ Reaction removed');
            
          } else if (data.action === 'replaced') {
            newReactions = oldReactions.filter(r => r.userId !== this.currentUserId);
            
            const normalizedReaction = {
              id: data.reaction.id,
              messageId: data.reaction.messageId,
              userId: data.reaction.userId,
              emoji: data.reaction.emoji,
              user: data.reaction.user ? {
                id: data.reaction.user.id,
                email: data.reaction.user.email,
                persons_info: {
                  name: data.reaction.user.persons_info?.name || '',
                  surname: data.reaction.user.persons_info?.surname || ''
                }
              } : null
            };
            
            newReactions = [...newReactions, normalizedReaction];
            console.log(`🔄 Reaction replaced → ${emoji}`);
            
          } else if (data.action === 'added') {
            newReactions = oldReactions.filter(r => r.userId !== this.currentUserId);
            
            const normalizedReaction = {
              id: data.reaction.id,
              messageId: data.reaction.messageId,
              userId: data.reaction.userId,
              emoji: data.reaction.emoji,
              user: data.reaction.user ? {
                id: data.reaction.user.id,
                email: data.reaction.user.email,
                persons_info: {
                  name: data.reaction.user.persons_info?.name || '',
                  surname: data.reaction.user.persons_info?.surname || ''
                }
              } : null
            };
            
            newReactions = [...newReactions, normalizedReaction];
            console.log('➕ Reaction added');
          }
          
          const newMsg = {
            id: oldMsg.id,
            chatId: oldMsg.chatId,
            senderId: oldMsg.senderId,
            text: oldMsg.text,
            type: oldMsg.type,
            replyTo: oldMsg.replyTo,
            forwardedFrom: oldMsg.forwardedFrom,
            isEdited: oldMsg.isEdited,
            isDeleted: oldMsg.isDeleted,
            status: oldMsg.status,
            createdAt: oldMsg.createdAt,
            updatedAt: oldMsg.updatedAt,
            sender: oldMsg.sender ? { ...oldMsg.sender } : null,
            parentMessage: oldMsg.parentMessage ? { ...oldMsg.parentMessage } : null,
            reactions: newReactions
          };
          
          this.messages[msgIndex] = newMsg;
          
          console.log('✅ Message updated, reactions count:', newReactions.length);
        }
      });
    } catch (e) {
      console.error('❌ Error toggling reaction:', e);
    }
  }

  setForwardMessage(messageId) {
    runInAction(() => {
      this.forwardMessageId = messageId;
      this.showForwardModal = true;
    });
  }

  hideForwardModal() {
    runInAction(() => {
      this.showForwardModal = false;
      this.forwardMessageId = null;
    });
  }

  async forwardMessage(targetChatId) {
    if (!this.forwardMessageId) return;
    
    try {
      await ForwardMessage(this.forwardMessageId, targetChatId);
      
      this.socket.emit('forward_message', {
        messageId: this.forwardMessageId,
        targetChatId
      });
      
      this.hideForwardModal();
    } catch (e) {
      console.error('Error forwarding message:', e);
    }
  }

  async openChat(chatItem) {
    runInAction(() => { 
      this.activeChat = chatItem;
      this.messages = [];
    });
    
    try {
      const data = await GetChatMessages(chatItem.id);
      
      runInAction(() => { 
        this.messages = (data.messages || []).map(msg => {
          if (msg.reactions && msg.reactions.length > 0) {
            console.log('📋 Message reactions structure:', msg.reactions[0]);
          }
          
          return {
            id: msg.id,
            chatId: msg.chatId,
            senderId: msg.senderId,
            text: msg.text,
            type: msg.type,
            replyTo: msg.replyTo,
            forwardedFrom: msg.forwardedFrom,
            isEdited: msg.isEdited,
            isDeleted: msg.isDeleted,
             isHtml: msg.isHtml || false,
            status: msg.status,
            createdAt: msg.createdAt,
            updatedAt: msg.updatedAt,
            sender: msg.sender ? { ...msg.sender } : null,
            parentMessage: msg.parentMessage ? { ...msg.parentMessage } : null,
            reactions: msg.reactions ? msg.reactions.map(r => ({
              id: r.id,
              messageId: r.messageId,
              userId: r.userId,
              emoji: r.emoji,
              user: r.user ? {
                id: r.user.id,
                email: r.user.email,
                persons_info: r.user.persons_info ? {
                  name: r.user.persons_info.name || '',
                  surname: r.user.persons_info.surname || ''
                } : null
              } : null
            })) : []
          };
        });
        
        this.messages.forEach(msg => {
          if (msg.status) {
            this.messageStatuses.set(msg.id, msg.status);
          }
        });
        
        const chatInList = this.chats.find(c => c.id === chatItem.id);
        if (chatInList) {
          chatInList.unreadCount = 0;
        }
      });

      if (this.socket) {
        this.socket.emit('message_read', { chatId: chatItem.id });
      } else {
        console.warn('⚠️ Socket not connected, cannot emit message_read');
      }
    } catch (e) {
      console.error('Error loading messages:', e);
    }
  }

  sendMessage(text) {
    if (!this.socket || !this.activeChat || !text.trim()) {
      console.warn('⚠️ Cannot send message:', { 
        hasSocket: !!this.socket, 
        hasActiveChat: !!this.activeChat, 
        text 
      });
      return;
    }

    console.log('📤 Sending message:', text, 'replyTo:', this.replyingTo);

    const tempMessage = {
      id: 'temp_' + Date.now(),
      chatId: this.activeChat.id,
      senderId: this.currentUserId,
      text: text.trim(),
      type: 'text',
      replyTo: this.replyingTo?.id || null,
      createdAt: new Date().toISOString(),
      sender: {
        id: this.currentUserId,
        persons_info: { name: 'Вы', surname: '' }
      }
    };

    if (this.replyingTo) {
      tempMessage.parentMessage = {
        id: this.replyingTo.id,
        text: this.replyingTo.text,
        senderId: this.replyingTo.senderId,
        sender: this.replyingTo.sender
      };
      console.log('✅ Added parentMessage to temp message:', tempMessage.parentMessage);
    }
    
    runInAction(() => {
      this.messages.push(tempMessage);
      this.messageStatuses.set(tempMessage.id, 'sent');
    });

    if (this.socket) {
      this.socket.emit('send_message', {
        chatId: this.activeChat.id,
        text: text.trim(),
        type: 'text',
        replyTo: this.replyingTo?.id || null
      });
    } else {
      console.error('❌ Socket not connected!');
    }

    runInAction(() => {
      this.replyingTo = null;
    });
  }

  toggleWidget() {
    // В PWA режиме не даём закрывать
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    
    if (isStandalone && this.isWidgetOpen) {
      console.log('⚠️ Cannot close widget in PWA mode');
      return;
    }
    
    runInAction(() => {
      this.isWidgetOpen = !this.isWidgetOpen;
      if (!this.isWidgetOpen) {
        this.activeChat = null;
        this.messages = [];
        this.view = 'chats';
        this.showRightSidebar = false;
      }
    });
  }

  openWidget() {
    runInAction(() => {
      this.isWidgetOpen = true;
    });
  }

  closeWidget() {
    // В PWA режиме не даём закрывать
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    
    if (isStandalone) {
      console.log('⚠️ Cannot close widget in PWA mode');
      return;
    }
    
    runInAction(() => {
      this.isWidgetOpen = false;
      this.activeChat = null;
      this.messages = [];
      this.view = 'chats';
      this.showRightSidebar = false;
      this.mobileTab = 'chats';
    });
  }

  setActiveChatNull() {
    runInAction(() => {
      this.activeChat = null;
      this.messages = [];
    });
  }

  shouldShowStatus(messageId) {
    const msgIndex = this.messages.findIndex(m => m.id === messageId);
    if (msgIndex === -1) return false;
    
    const msg = this.messages[msgIndex];
    const nextMsg = this.messages[msgIndex + 1];
    
    if (String(msg.senderId) !== String(this.currentUserId)) return false;
    if (msg.id.startsWith('temp_')) return false;
    if (!nextMsg) return true;
    if (String(nextMsg.senderId) !== String(msg.senderId)) return true;
    
    const timeDiff = new Date(nextMsg.createdAt) - new Date(msg.createdAt);
    if (timeDiff > 5 * 60 * 1000) return true;
    
    return false;
  }

  get totalUnread() {
    return this.chats.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  }

  async requestNotifications() {
    const granted = await requestNotificationPermission();
    console.log('🔔 Notification permission:', granted ? 'GRANTED' : 'DENIED');
    return granted;
  }

  sendTyping() {
    if (!this.socket || !this.activeChat) return;
    this.socket.emit('typing', { chatId: this.activeChat.id });
  }

  sendStopTyping() {
    if (!this.socket || !this.activeChat) return;
    this.socket.emit('stop_typing', { chatId: this.activeChat.id });
  }

  getMessageStatus(messageId) {
    return this.messageStatuses.get(messageId) || 'sent';
  }

  get typingText() {
    if (this.typingUsers.size === 0) return '';
    
    const users = Array.from(this.typingUsers.values());
    
    if (users.length === 1) {
      const name = users[0].name || 'Собеседник';
      const surname = users[0].surname || '';
      return `${name} ${surname}`.trim() + ' печатает...';
    }
    
    if (users.length === 2) {
      const names = users.map(u => u.name || 'Собеседник');
      return `${names[0]} и ${names[1]} печатают...`;
    }
    
    return `${users.length} человек печатают...`;
  }
}